import {
  DashboardState,
  EColorPickerTab,
  ESdChangeReason,
  IFillStyle,
  IImageLibraryDto,
  IKuikaStyle,
  InvisibleStylePart,
  Visibility
} from "@kuika/kuika-cl-model";
import { Form } from "antd/es";
import { FormInstance } from "antd/es/form";
import classNames from "classnames";
import { Guid } from "guid-typescript";
import _ from "lodash";
import { ImageLibraryService } from "../../../../services/image-library-service";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { ColorInputHelper } from "../../../../shared/utility/color-input-helper";
import { ColorPickerPositionHelper } from "../../../../shared/utility/color-picker-position-helper";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { ScreenDesignHelper } from "../../../../shared/utility/screen-design-helper";
import { DeleteModal } from "../../../common/delete-modal";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import StyleLabel from "./style-label/style-label";
import { ColorPicker } from "./styling-color-picker";
import { StylingSelector } from "./styling-selector/styling-selector";
import { styleSectionNeedsRender } from "./styling-window-helpers";

export interface IDeleteArguments {
  isDeleteStyleModalVisible: boolean;
  deletingStyleName: string;
  performStyleTagOnClose?: () => void;
}

export interface IFillComponentState {
  isColorPickerActive: boolean;
  isImagePickerActive: boolean;
  allImage: IImageLibraryDto[];
  isDataLoading: boolean;
  isActiveDragger: boolean;
  tempData: IImageLibraryDto[];
  searchText: string;
  deleteArguments: IDeleteArguments;
}

export interface IFillComponentProps {
  form: FormInstance<IKuikaStyle>;
  // KOA-15838: true while a style tag is being edited; drives the blue (token)
  // vs orange (locally changed) label colour independently of the form's section
  // id (which a state switch nulls).
  isEditMode?: boolean;
}

export enum EditMode {
  custom = 1,
  edit = 2,
  create = 3
}

class FillComponent extends PureSdComponent<
  IFillComponentProps & IWithSDProps & IWithContextProp,
  IFillComponentState
> {
  constructor(props: IFillComponentProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {
      isColorPickerActive: false,
      isImagePickerActive: false,
      allImage: [],
      isDataLoading: true,
      isActiveDragger: false,
      tempData: [],
      searchText: "",
      deleteArguments: { isDeleteStyleModalVisible: false, deletingStyleName: "", performStyleTagOnClose: undefined }
    };
  }

  toggleColorPicker = (event?: any) => {
    if (this.isStyleHidden(InvisibleStylePart.Fill_Color)) {
      return;
    }
    this.setState({
      isColorPickerActive: !this.state.isColorPickerActive,
      isImagePickerActive: false
    });
    if (event) {
      if (typeof event.persist === "function") {
        event.persist();
      }
      ColorPickerPositionHelper.saveLastClickEvent(event);

      KMainFunctions.setColorTriggeredPickerPosition(
        event,
        this.props.form.getFieldValue(["fill", "color"])?.includes("gradient")
          ? EColorPickerTab.Gradient
          : EColorPickerTab.Solid
      );
    }
  };

  toggleImagePicker = (event?: any) => {
    if (this.isStyleHidden(InvisibleStylePart.Fill_BackgroundImage)) {
      return;
    }
    this.setState({
      isImagePickerActive: !this.state.isImagePickerActive,
      isColorPickerActive: false
    });
    if (event) {
      if (typeof event.persist === "function") {
        event.persist();
      }
      ColorPickerPositionHelper.saveLastClickEvent(event);
    }
  };

  handleLeftUnitClick = (event?: any) => {
    const inputElement = event?.target?.closest(".kuika-styling-panel__color-input-wrapper")?.querySelector("input");
    if (inputElement) {
      inputElement.focus();
    }
    this.toggleColorPicker(event);
  };

  handleFillColorClear = () => {
    const values = this.props.form.getFieldValue("fill");
    if (values) {
      values.color = undefined;
    }
    this.props.form.setFieldsValue({ fill: values });
    this.props.form.submit();
  };

  closeColorPicker = () => {
    this.setState({ isColorPickerActive: false });
  };

  closeImagePicker = () => {
    this.setState({ isImagePickerActive: false });
  };

  openColorPicker = () => {
    this.setState({ isColorPickerActive: true });
  };

  openImagePicker = () => {
    this.setState({ isImagePickerActive: true });
  };

  getImageLibrary = async () => {
    this.setState({ isDataLoading: true });
    try {
      const res = await ImageLibraryService.GetAll();
      if (res && res.status === 200) {
        this.setState({ allImage: res.data });
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.setState({ isDataLoading: false });
    }
  };

  componentDidMount = () => {
    this.getImageLibrary();
  };

  componentDidUpdate = (prevProps: IFillComponentProps & IWithSDProps & IWithContextProp) => {
    const mode: EditMode = this.getCurrentMode();
    if (mode !== EditMode.custom && prevProps.sd.selectedKuikaIds[0] !== this.props.sd.selectedKuikaIds[0]) {
      this.clearProjectTempStyle();
      this.setFillCustomStyle();
    }
    if (prevProps.sd.selectedKuikaIds[0] !== this.props.sd.selectedKuikaIds[0]) {
      this.closeColorPicker();
      this.closeImagePicker();
    }
  };

  submitForm = (fieldsValue: IKuikaStyle) => {
    this.props.form.resetFields();
    this.props.form.setFieldsValue(fieldsValue);
    this.props.form.submit();
  };

  new = async (name: string) => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    if (!fieldsValue.fill) {
      return;
    }

    const project = _.cloneDeep(this.props.sd.project);

    let stylingFills = {} as IFillStyle;
    stylingFills.fillId = Guid.create().toString();

    const selectedDi = this.props.sd.getSelectedDisplayItem(this.props.sd.getScreenDesign());
    const selectedDIItem = selectedDi?.item;
    stylingFills.name = name;
    stylingFills.componentType = selectedDIItem?.name;

    stylingFills.styleType = "custom";

    const formValues = {
      color: fieldsValue.fill.color,
      backgroundImage: fieldsValue.fill.backgroundImage,
      repeatSwitch: fieldsValue.fill.repeatSwitch,
      bgImageSize: fieldsValue.fill.bgImageSize,
      stretch: fieldsValue.fill.stretch,
      imageFit: fieldsValue.fill.imageFit,
      bgImagePosition: fieldsValue.fill.bgImagePosition,
      isVisible: fieldsValue.fill.isVisible
    };

    stylingFills = {
      ...stylingFills,
      ...formValues
    };

    if (!project.stylingFill) {
      project.stylingFill = [];
    }
    project.stylingFill.push(stylingFills);

    const savedId = stylingFills.fillId;

    await this.props.sd.updateProject(project, true, ESdChangeReason.ChangeProjectStyle);

    const updatedFieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    if (updatedFieldsValue.fill?.fillIds && savedId) {
      updatedFieldsValue.fill.fillIds.push(savedId);
      updatedFieldsValue.fill.fillId = savedId;
      updatedFieldsValue.fill.name = name;
      this.submitForm(updatedFieldsValue);
    }
  };

  onBgImageChange = (img: IImageLibraryDto) => {
    const values = this.props.form.getFieldValue("fill");
    if (values) {
      values.backgroundImage = img.id;
    }
    this.props.form.setFieldsValue({ fill: values });
    this.props.form.submit();
  };

  setBgImageNone = () => {
    const values = this.props.form.getFieldValue("fill");
    if (values) {
      values.backgroundImage = undefined;
    }
    this.props.form.setFieldsValue({ fill: values });
    this.props.form.submit();
  };

  handleIsVisibleDragger = (val: string) => {
    if (val === "add") {
      this.setState({ isActiveDragger: true });
    } else if (val === "cancel") {
      this.setState({ isActiveDragger: false });
    }
  };

  handleBgImageSizeChange = (checked: boolean) => {
    if (checked) {
      const values = this.props.form.getFieldValue("fill");
      values.repeatSwitch = !checked;
      this.props.form.setFieldsValue({ fill: values });
    }
  };

  handleBgImagePositionChange = (value: string) => {
    const values = this.props.form.getFieldValue("fill");
    values.bgImagePosition = value;
    this.props.form.setFieldsValue({ fill: values });
    this.props.form.submit();
  };

  handleFillChange = (checked: boolean) => {
    if (checked) {
      const values = this.props.form.getFieldValue("fill");
      values.bgImageSize = !checked;
      this.props.form.setFieldsValue({ fill: values });
    }
  };

  handleStretchChange = (checked: boolean) => {
    if (checked) {
      const values = this.props.form.getFieldValue("fill");
      values.stretch = true;
      values.bgImageSize = false;
      values.repeatSwitch = false;
      this.props.form.setFieldsValue({ fill: values });
    }
  };

  handleSelectedTagRemoved = (label: string) => {
    if (label !== undefined && label.length > 0 && this.props?.sd?.project?.stylingFill) {
      const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
      const stylingFillDefList = this.props.sd.project.stylingFill;
      const tempValue = stylingFillDefList.filter((item) => item.name === label || item.fillId === label);
      if (tempValue && tempValue.length > 0 && fieldsValue.fill) {
        if (tempValue[0].fillId === fieldsValue.fill.fillId) {
          this.setFillCustomStyle();
        }
      }
    }
    return true;
  };

  handleFillStylingChange = (value: string | number | Array<string | number> | null) => {
    if (!value || !Array.isArray(value) || value.length === 0) return;

    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();

    if (fieldsValue.fill) {
      fieldsValue.fill.fillIds = value as string[];

      this.props.form.setFieldsValue(fieldsValue);

      this.props.form.submit();
    }
  };

  setFillCustomStyle = () => {
    const { backupCustomFill } = this.props.sd;
    if (backupCustomFill?.kuikaid === this.props.sd.selectedKuikaIds[0]) {
      const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
      if (fieldsValue?.fill && backupCustomFill) {
        fieldsValue.fill.fillId = null;
        fieldsValue.fill.name = backupCustomFill.name;
        fieldsValue.fill.componentType = backupCustomFill.componentType;
        fieldsValue.fill.isVisible = backupCustomFill.isVisible;
        fieldsValue.fill.color = backupCustomFill.color;
        fieldsValue.fill.backgroundImage = backupCustomFill.backgroundImage;
        fieldsValue.fill.repeatSwitch = backupCustomFill.repeatSwitch;
        fieldsValue.fill.bgImageSize = backupCustomFill.bgImageSize;
        fieldsValue.fill.stretch = backupCustomFill.stretch;
        fieldsValue.fill.imageFit = backupCustomFill.imageFit;
        fieldsValue.fill.bgImagePosition = backupCustomFill.bgImagePosition;

        this.clearProjectTempStyle();
        this.submitForm(fieldsValue);
      } else {
        this.clearStylingFill();
      }
    } else {
      this.clearProjectTempStyle();
    }
  };

  clearProjectTempStyle = async () => {
    const { projectTempStyles } = this.props.sd;
    projectTempStyles.tempStylingFill = undefined;
    this.props.sd.setProjectTempStyles(projectTempStyles);
    this.props.sd.setCustomFillStyleBackup(undefined);
  };

  clearStylingFill = () => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    if (fieldsValue?.fill) {
      fieldsValue.fill.fillId = null;
      fieldsValue.fill.name = undefined;
      fieldsValue.fill.isVisible = undefined;
      fieldsValue.fill.color = undefined;
      fieldsValue.fill.backgroundImage = undefined;
      fieldsValue.fill.repeatSwitch = undefined;
      fieldsValue.fill.stretch = undefined;
      fieldsValue.fill.imageFit = undefined;
      fieldsValue.fill.bgImageSize = undefined;
      fieldsValue.fill.bgImagePosition = undefined;
    }
    this.submitForm(fieldsValue);
  };

  isDefaultStying = (stylingId: string) => {
    let found = false;
    this.props.sd.defaultStyleProvider?.getDesktopStyles().fill.forEach((styling) => {
      if (styling.fillId?.toLowerCase() === stylingId?.toLowerCase() || styling.name === stylingId) found = true;
    });
    return found;
  };

  setFillStyle = (val: IFillStyle) => {
    if (val) {
      const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
      if (fieldsValue?.fill) {
        fieldsValue.fill.fillId = val.fillId;
        fieldsValue.fill.name = val.name;
        fieldsValue.fill.componentType = val.componentType;
        fieldsValue.fill.isVisible = val.isVisible;
        fieldsValue.fill.color = val.color;
        fieldsValue.fill.backgroundImage = val.backgroundImage;
        fieldsValue.fill.repeatSwitch = val.repeatSwitch;
        fieldsValue.fill.stretch = val.stretch;
        fieldsValue.fill.imageFit = val.imageFit;
        fieldsValue.fill.bgImageSize = val.bgImageSize;
        fieldsValue.fill.bgImagePosition = val.bgImagePosition;

        this.submitForm(fieldsValue);
      }
    }
  };

  edit = (label: string | undefined) => {
    const mode: EditMode = this.getCurrentMode();
    const editDelegate = () => {
      if (label !== undefined && label.length > 0 && this.props?.sd?.project?.stylingFill) {
        const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
        const fillBackup = _.clone(fieldsValue.fill) as IFillStyle;
        const [kuikaId] = this.props.sd.selectedKuikaIds;
        fillBackup.kuikaid = kuikaId;
        this.props.sd.setCustomFillStyleBackup(fillBackup);
        const stylingFillDefList = this.props.sd.project.stylingFill;
        const tempValue = stylingFillDefList.filter((item) => item.name === label);
        if (tempValue && tempValue.length > 0) {
          this.setFillStyle(tempValue[0]);
        }
      }
    };
    if (mode === EditMode.custom) {
      editDelegate();
    } else {
      this.setFillCustomStyle();
      editDelegate();
    }
  };

  duplicate = (label: string | undefined) => {
    const mode: EditMode = this.getCurrentMode();
    const duplicateDelegate = async () => {
      if (label !== undefined && label.length > 0 && this.props?.sd?.project?.stylingFill) {
        const stylingFillDefList = this.props.sd.project.stylingFill;
        const tempValue = stylingFillDefList.filter((item) => item.name === label);
        if (tempValue && tempValue.length > 0) {
          const copyValue = _.cloneDeep(tempValue[0]);
          copyValue.fillId = Guid.create().toString();
          copyValue.name = `${label}_copy`;
          copyValue.styleType = "custom";
          delete copyValue.overridden;

          let unique = false;
          while (!unique) {
            let found = false;
            this.props.sd.project.stylingFill?.forEach((stylingFill) => {
              if (stylingFill?.name === copyValue.name) {
                copyValue.name += "_copy";
                found = true;
              }
            });
            unique = !found;
          }

          const project = _.cloneDeep(this.props.sd.project);
          project.stylingFill?.push(copyValue);
          const savedId = copyValue.fillId;
          await this.props.sd.updateProject(project, true, ESdChangeReason.ChangeProjectStyle);
          const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
          if (fieldsValue.fill?.fillIds && savedId) {
            fieldsValue.fill.fillIds.push(savedId);
            this.submitForm(fieldsValue);
          }
        }
      }
    };
    if (mode === EditMode.custom) {
      duplicateDelegate();
    } else {
      this.setFillCustomStyle();
      duplicateDelegate();
    }
  };

  cancel = () => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    if (fieldsValue?.fill) {
      fieldsValue.fill.fillId = null;
      fieldsValue.fill.name = "";
      this.submitForm(fieldsValue);
    }
    this.setFillCustomStyle();
  };

  save = async (name?: string, _mode?: EditMode) => {
    const mode: EditMode = _mode || this.getCurrentMode();
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();

    if (!fieldsValue.fill) {
      return;
    }

    if (name && name.trim().length === 0) {
      KMainFunctions.displayWarningNotification("Please write a name.");
      return;
    }

    if (mode === EditMode.create) {
      const isNameExist = this.props.sd.project.stylingFill
        ?.map((s) => s.name)
        .includes(name || fieldsValue.fill?.name);
      if (isNameExist === true) {
        KMainFunctions.displayWarningNotification(
          "There cannot be another registration with the same name. Please write a different name."
        );
        return;
      }
    }

    const project = _.cloneDeep(this.props.sd.project);

    let stylingFills = {} as IFillStyle;
    if (mode === EditMode.edit) {
      const data = project.stylingFill;
      const selectedFill = fieldsValue?.fill?.fillId;
      if (selectedFill && data && fieldsValue.fill) {
        const [stylingFill] = data.filter((item) => item.fillId === selectedFill);
        stylingFills = stylingFill;
      }
    }

    const selectedDi = this.props.sd.getSelectedDisplayItem(this.props.sd.getScreenDesign());
    const selectedDIItem = selectedDi?.item;

    if (mode === EditMode.create) {
      stylingFills.fillId = Guid.create().toString();
    }
    stylingFills.name = name || fieldsValue.fill.name;
    stylingFills.componentType = selectedDIItem?.name;
    stylingFills.isVisible = fieldsValue.fill.isVisible;

    const formStyleValues = {
      color: fieldsValue.fill.color,
      backgroundImage: fieldsValue.fill.backgroundImage,
      repeatSwitch: fieldsValue.fill.repeatSwitch,
      bgImageSize: fieldsValue.fill.bgImageSize,
      stretch: fieldsValue.fill.stretch,
      imageFit: fieldsValue.fill.imageFit,
      bgImagePosition: fieldsValue.fill.bgImagePosition
    };

    if (stylingFills.styleType !== "custom" && mode === EditMode.edit) {
      const overridden = stylingFills.overridden || [];

      Object.keys(formStyleValues).forEach((key) => {
        if (!overridden.includes(key)) {
          if (formStyleValues[key] !== stylingFills[key]) {
            overridden.push(key);
          }
        }
      });

      stylingFills.overridden = overridden;
    } else {
      stylingFills.styleType = "custom";
      delete stylingFills.overridden;
    }

    stylingFills = {
      ...stylingFills,
      ...formStyleValues
    };

    if (!project.stylingFill) {
      project.stylingFill = [];
    }
    const existingStyleIndex = project.stylingFill?.findIndex((p) => p.fillId === stylingFills.fillId);
    if (existingStyleIndex >= 0) {
      project.stylingFill[existingStyleIndex] = stylingFills;
    } else {
      project.stylingFill?.push(stylingFills);
    }
    if (mode === EditMode.create) {
      const savedId = stylingFills.fillId;
      await this.props.sd.updateProject(project, true, ESdChangeReason.ChangeProjectStyle);
      this.clearStylingFill();
      if (fieldsValue.fill?.fillIds && savedId) {
        fieldsValue.fill.fillIds.push(savedId);
        this.submitForm(fieldsValue);
        this.setFillCustomStyle();
        this.cancel();
      }
    } else {
      await this.props.sd.updateProject(project, true, ESdChangeReason.ChangeProjectStyle);
      this.setFillCustomStyle();
    }
  };

  getCurrentMode = () => {
    let mode: EditMode = EditMode.custom;
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    if (fieldsValue.fill?.fillId && fieldsValue.fill.fillId.length > 0) {
      if (fieldsValue.fill.fillId === "-1") {
        mode = EditMode.create;
      } else {
        mode = EditMode.edit;
      }
    }
    return mode;
  };

  static removeFromComponent = (performTagOnClose: any) => {
    if (performTagOnClose) {
      performTagOnClose();
    }
  };

  getFriendlyName = (): string => {
    const selectedDi = this.props.sd.getSelectedDisplayItem(this.props.sd.getScreenDesign());
    return selectedDi?.friendlyName || "";
  };

  deleteFromProjectConfirmation = (label: string | undefined, performTagOnClose: any) => {
    if (label) {
      this.setState({
        deleteArguments: {
          deletingStyleName: label,
          isDeleteStyleModalVisible: true,
          performStyleTagOnClose: performTagOnClose
        }
      });
    }
  };

  deleteFromProject = async () => {
    const { deletingStyleName, performStyleTagOnClose } = this.state.deleteArguments;

    if (!deletingStyleName) {
      return;
    }

    const currentFill = this.props.sd.project.stylingFill.find((item) => item.name === deletingStyleName);

    if (!currentFill) {
      KMainFunctions.displayWarningNotification("Style not found.");
      return;
    }

    const currentFillId: any = currentFill.fillId;

    const isDefault = this.isDefaultStying(currentFillId);

    if (isDefault) {
      KMainFunctions.displayWarningNotification(
        "You cannot remove default styling from the project. If you wish, you can remove it from this component."
      );
      return;
    }

    const deleteDelegate = async () => {
      const project = _.cloneDeep(this.props.sd.project);

      project.stylingFill = project.stylingFill.filter((item) => item.name !== deletingStyleName);

      await this.props.sd.updateProject(project, true, ESdChangeReason.ChangeProjectStyle);

      const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
      if (fieldsValue.fill?.fillIds) {
        fieldsValue.fill.fillIds = fieldsValue.fill.fillIds.filter((id) => id !== currentFillId);
        this.submitForm(fieldsValue);
      }

      if (performStyleTagOnClose) {
        performStyleTagOnClose();
      }

      this.setState({
        deleteArguments: {
          isDeleteStyleModalVisible: false,
          deletingStyleName: "",
          performStyleTagOnClose: undefined
        }
      });
    };

    deleteDelegate();
  };

  isCurrentComponentsDefaultStying = (styingId: string) => {
    const selectedDi = this.props.sd.getSelectedDisplayItem(this.props.sd.getScreenDesign());

    return (
      styingId?.toLowerCase() ===
      ScreenDesignHelper.getDefaultStylingForMobile(
        selectedDi!,
        "fill",
        this.props.sd.defaultStyleProvider!
      )?.toLowerCase()
    );
  };

  onImageFitChange = (e) => {
    const values = this.props.form.getFieldValue("fill");
    values.bgImageSize = false;
    values.stretch = false;
    values.repeatSwitch = false;
    if (e === "fit") {
      values.bgImageSize = true;
    } else if (e === "repeat") {
      values.repeatSwitch = true;
    } else if (e === "stretch") {
      values.stretch = true;
    }
    values.imageFit = e;
    this.props.form.setFieldsValue({ fill: values });
    this.props.form.submit();
  };

  handleColorChange = (color: string) => {
    const values = this.props.form.getFieldValue("fill");
    values.color = color;
    this.props.form.setFieldsValue({ fill: values });
    this.props.form.submit();
  };

  handleColorInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    ColorInputHelper.handleColorInputChange(e.target.value, this.handleColorChange);
  };

  hexToRgba = (hex: string): string | null => {
    return ColorInputHelper.hexToRgba(hex);
  };

  handleOpacityChange = (e) => {
    const values = this.props.form.getFieldValue("fill");
    if (!values.color) return;

    values.color = ColorInputHelper.updateColorOpacity(values.color, e.target.value);
    this.props.form.setFieldsValue({ fill: values });
    this.props.form.submit();
  };

  hasValue = (fieldPath: string[]) => {
    const value = this.props.form.getFieldValue(fieldPath);
    if (value === "transparent") {
      return false;
    }
    return value !== undefined && value !== null && value !== "";
  };

  getStyleClassNames = (fieldPaths: string[], baseClassName?: string) => {
    const mode: EditMode = this.getCurrentMode();
    const hasAnyValue = fieldPaths.some((path) => this.hasValue(["fill", path]));
    // KOA-15838: editing a style tag means the value IS a token value → blue
    // (`--has-value`), not the orange "changed" dot — even after a state switch
    // nulls the section id (which would flip getCurrentMode to custom).
    const isTokenValue: boolean = this.props.isEditMode === true || mode === EditMode.edit;

    return classNames(baseClassName, {
      "kuika-styling-panel__label--has-value": isTokenValue && hasAnyValue,
      "kuika-styling-panel__label--changed": !isTokenValue && hasAnyValue
    });
  };

  isStyleHidden = (styleName: InvisibleStylePart): boolean => {
    return this.props.sd.getStylesVisibility(styleName) === Visibility.Hidden;
  };

  static findStartWithFill = (arg: string[]) => {
    const joined: string[] = [];
    arg.forEach((value) => {
      if (value.startsWith("fill_")) {
        joined.push(value.substring(5));
      }
    });
    return joined;
  };

  resetSectionValues = (fieldPaths: string[]) => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    fieldPaths.forEach((path) => {
      if (fieldsValue.fill && fieldsValue.fill[path]) {
        fieldsValue.fill[path] = undefined;
      }
    });
    this.submitForm(fieldsValue);
  };

  render() {
    const friendlyName = this.getFriendlyName();
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    const mode: EditMode = this.getCurrentMode();
    const selectedDi = this.props.sd.getSelectedDisplayItem(this.props.sd.getScreenDesign());
    const isReportDesigner: boolean = this.props.sd.dashboardState === DashboardState.reportDesigner;

    const stylingVersionMenu = (label: string, performTagOnClose: any) => {
      const isDefaultStyling = this.isDefaultStying(label);
      return [
        {
          key: "1",
          label: "Edit style",
          icon: <KuikaSvg width={16} height={16} name="settings" type="sidebar-icons" />,
          onClick: () => this.edit(label)
        },
        {
          key: "2",
          label: "Duplicate",
          icon: <KuikaSvg width={16} height={16} name="copy-plus" />,
          onClick: () => this.duplicate(label)
        },
        {
          key: "3",
          label: `Remove from '${friendlyName}'`,
          icon: <KuikaSvg width={16} height={16} name="x" />,
          onClick: () => FillComponent.removeFromComponent(performTagOnClose)
        },
        {
          key: "4",
          label: "Delete from project",
          icon: <KuikaSvg width={16} height={16} name="trash" />,
          onClick: () => this.deleteFromProjectConfirmation(label, performTagOnClose),
          danger: true,
          hidden: isDefaultStyling
        }
      ];
    };

    const getOpacityFromColor = (color: string) => {
      return ColorInputHelper.getOpacityFromColor(color);
    };

    return (
      <>
        <DeleteModal
          itemName={this.state.deleteArguments.deletingStyleName}
          triggerModal={this.state.deleteArguments.isDeleteStyleModalVisible}
          deleteItem={() => this.deleteFromProject()}
          deleteType={"Style"}
          closeModal={() =>
            this.setState({
              deleteArguments: {
                isDeleteStyleModalVisible: false,
                deletingStyleName: "",
                performStyleTagOnClose: undefined
              }
            })
          }
        />
        <StylingSelector
          formItemName={["fill", "fillIds"]}
          stylingList={this.props?.sd?.project?.stylingFill}
          idField="fillId"
          handleSelectedTagRemoved={this.handleSelectedTagRemoved}
          isDefaultStyling={this.isDefaultStying}
          isCurrentComponentDefaultStyling={this.isCurrentComponentsDefaultStying}
          stylingVersionMenu={stylingVersionMenu}
          defaultStylingRegex={/KUIKA-[a-zA-Z]+-DEFAULT-FILL(#[a-zA-Z]+#)*$/gm}
          new={this.new}
          cancel={this.cancel}
          save={this.save}
          edit={this.edit}
          mode={mode}
          form={this.props.form}
          onChange={this.handleFillStylingChange}
          hide
        />
        <div
          className={classNames("kuika-styling-panel__fill-form", {
            "kuika-styling-panel__editing": mode === EditMode.edit
          })}
        >
          <Form.Item name={["fill", "fillId"]} noStyle shouldUpdate>
            <input type="hidden"></input>
          </Form.Item>
          <Form.Item name={["fill", "componentType"]} noStyle shouldUpdate>
            <input type="hidden"></input>
          </Form.Item>
          <Form.Item name={["fill", "color"]} noStyle shouldUpdate>
            <input type="hidden"></input>
          </Form.Item>
          <Form.Item name={["fill", "backgroundImage"]} noStyle shouldUpdate>
            <input type="hidden"></input>
          </Form.Item>
          <Form.Item name={["fill", "imageFit"]} noStyle shouldUpdate>
            <input type="hidden"></input>
          </Form.Item>
          <Form.Item name={["fill", "bgImagePosition"]} noStyle shouldUpdate>
            <input type="hidden"></input>
          </Form.Item>
          <Form.Item name={["fill", "stretch"]} noStyle shouldUpdate>
            <input type="hidden"></input>
          </Form.Item>
          <Form.Item name={["fill", "repeatSwitch"]} noStyle shouldUpdate>
            <input type="hidden"></input>
          </Form.Item>
          <Form.Item name={["fill", "bgImageSize"]} noStyle shouldUpdate>
            <input type="hidden"></input>
          </Form.Item>
          <Form.Item name={["fill", "name"]} noStyle shouldUpdate>
            <input type="hidden"></input>
          </Form.Item>
          <div
            style={{
              display: this.state.isColorPickerActive === true ? "block" : "none"
            }}
            className="kuika_colorPicker"
          >
            <Form.Item name={["fill", "color"]} valuePropName="color" noStyle>
              <ColorPicker
                onCancel={this.closeColorPicker}
                isColorPickerActive={this.state.isColorPickerActive}
                onChange={this.handleColorChange}
                pickerType="color"
              />
            </Form.Item>
          </div>

          <div
            style={{
              display: this.state.isImagePickerActive === true ? "block" : "none"
            }}
            className="kuika_colorPicker"
          >
            <Form.Item name={["fill", "backgroundImage"]} noStyle>
              <ColorPicker
                onCancel={this.closeImagePicker}
                isColorPickerActive={this.state.isImagePickerActive}
                onImageFitChange={this.onImageFitChange}
                handleBgImagePositionChange={this.handleBgImagePositionChange}
                onBgImageChange={this.onBgImageChange}
                setBgImageNone={this.setBgImageNone}
                initialImageId={fieldsValue?.fill?.backgroundImage}
                initialImageFit={fieldsValue?.fill?.imageFit}
                initialImagePosition={fieldsValue?.fill?.bgImagePosition}
                allImage={this.state.allImage}
                getImageLibrary={this.getImageLibrary}
                isDataLoading={this.state.isDataLoading}
                pickerType="image"
              />
            </Form.Item>
          </div>
          <div className="kuika-styling-panel__form-field-wrapper">
            <StyleLabel
              fieldPaths={["color"]}
              section="color"
              hasValue={this.hasValue(["fill", "color"])}
              className={this.getStyleClassNames(["color"])}
              onReset={this.resetSectionValues}
              disabled={this.isStyleHidden(InvisibleStylePart.Fill_Color)}
            >
              Color
            </StyleLabel>
            <div className="kuika-styling-panel__color-input-wrapper kuika-styling-panel__input-wrapper">
              <Form.Item noStyle shouldUpdate>
                <KuikaInput
                  data-testid="fillAreaColorPicker"
                  placeholder={!this.props.form.getFieldValue(["fill", "color"]) ? "TRANSPARENT" : ""}
                  value={
                    this.props.form.getFieldValue(["fill", "color"])?.includes("gradient")
                      ? "Gradient"
                      : KMainFunctions.convertRgbaToHex(this.props.form.getFieldValue(["fill", "color"])) ||
                        "TRANSPARENT"
                  }
                  disabled={this.isStyleHidden(InvisibleStylePart.Fill_Color)}
                  className="kuika-styling-panel__color-input"
                  onClick={(e) => this.toggleColorPicker(e)}
                  onChange={this.handleColorInputChange}
                  leftUnit={
                    this.props.form.getFieldValue(["fill", "color"]) &&
                    this.props.form.getFieldValue(["fill", "color"]) !== "transparent" ? (
                      <div
                        onClick={(e) => this.handleLeftUnitClick(e)}
                        style={{
                          width: 24,
                          height: 24,
                          background: this.props.form.getFieldValue(["fill", "color"])
                        }}
                      ></div>
                    ) : (
                      <img
                        onClick={(e) => this.handleLeftUnitClick(e)}
                        src={"/images/styling-panel-icons/transparent.svg"}
                        alt="transparent"
                        className="kuika-styling-panel__transparent-icon"
                      />
                    )
                  }
                  rightUnit={
                    <div
                      className="kuika-styling-panel__color-input-unit"
                      style={{ display: "flex", alignItems: "center", gap: "4px" }}
                    >
                      {this.props.form.getFieldValue(["fill", "color"]) &&
                        this.props.form.getFieldValue(["fill", "color"]) !== "transparent" && (
                          <>
                            <KuikaSvg width={16} height={16} name="trash" onClick={this.handleFillColorClear} pointer />
                            <KuikaSmallText>HEX</KuikaSmallText>
                          </>
                        )}
                    </div>
                  }
                  size="sm"
                />
              </Form.Item>
              <KuikaInput
                disabled={this.isStyleHidden(InvisibleStylePart.Fill_Color)}
                type="number"
                size="sm"
                placeholder="100"
                rightUnit="%"
                className="kuika-styling-panel__color-input"
                defaultValue={getOpacityFromColor(this.props.form.getFieldValue(["fill", "color"]))}
                onChange={this.handleOpacityChange}
                min={0}
                max={100}
                onKeyDown={(e) => (KMainFunctions.allowOnlyNumbers(e) ? true : e.preventDefault())}
              />
            </div>
          </div>

          {!isReportDesigner && (
            <div className="kuika-styling-panel__form-field-wrapper">
              <StyleLabel
                fieldPaths={[
                  "backgroundImage",
                  "imageFit",
                  "bgImagePosition",
                  "stretch",
                  "repeatSwitch",
                  "bgImageSize"
                ]}
                section="image"
                hasValue={this.hasValue(["fill", "backgroundImage"])}
                className={this.getStyleClassNames(["backgroundImage"])}
                onReset={this.resetSectionValues}
                disabled={this.isStyleHidden(InvisibleStylePart.Fill_BackgroundImage)}
              >
                Image
              </StyleLabel>
              <div className="kuika-styling-panel__input-wrapper">
                <Form.Item noStyle shouldUpdate>
                  <KuikaInput
                    data-testid="fillAreaNoImageInput"
                    placeholder="No image"
                    readOnly
                    value={
                      this.state.allImage.find((img) => img.id === fieldsValue?.fill?.backgroundImage)?.fileName || ""
                    }
                    disabled={this.isStyleHidden(InvisibleStylePart.Fill_BackgroundImage)}
                    className="kuika-styling-panel__color-input"
                    onClick={(e) => this.toggleImagePicker(e)}
                    rightUnit={
                      fieldsValue?.fill?.backgroundImage && (
                        <div
                          className="kuika-styling-panel__color-input-unit"
                          style={{ display: "flex", alignItems: "center", gap: "4px" }}
                        >
                          <KuikaSvg width={16} height={16} name="trash" onClick={this.setBgImageNone} pointer />
                        </div>
                      )
                    }
                    size="sm"
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </div>
            </div>
          )}
        </div>
      </>
    );
  }

  // eslint-disable-next-line class-methods-use-this
  checkNeedRender = (changeReason: ESdChangeReason): boolean => styleSectionNeedsRender(changeReason);
}

const fillComponent = withContext<IFillComponentProps>(withSD<IFillComponentProps>(FillComponent));
export { fillComponent as FillComponent };
